import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { CheckCircleIcon, BellIcon, ArrowRightIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline'

interface PaymentRejectSuccessState {
  slip: {
    id: string
    invoiceId: string
    studentName: string
    parentName: string
    amount: number
  }
  reason: string
  rejectedAt: string
}

export const PaymentRejectSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as PaymentRejectSuccessState

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.slip) {
      navigate('/admin/payments', { replace: true })
    }
  }, [state, navigate])

  if (!state?.slip) {
    return null // Will redirect in useEffect
  }

  const formatThaiBaht = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatThaiDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ไม่อนุมัติเรียบร้อย"
        showBackButton={false}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ดำเนินการเรียบร้อย
          </h1>
          <p className="text-gray-600">
            ไม่อนุมัติการชำระเงินและแจ้งผู้ปกครองแล้ว
          </p>
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดการไม่อนุมัติ</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ใบแจ้งหนี้:</span>
              <span className="font-medium">{state.slip.invoiceId}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">นักเรียน:</span>
              <span className="font-medium">{state.slip.studentName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ผู้ปกครอง:</span>
              <span className="font-medium">{state.slip.parentName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">จำนวนเงิน:</span>
              <span className="font-medium text-blue-600">{formatThaiBaht(state.slip.amount)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">เวลาที่ไม่อนุมัติ:</span>
              <span className="font-medium">{formatThaiDateTime(state.rejectedAt)}</span>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <span className="text-gray-600 block mb-2">เหตุผลที่ไม่อนุมัติ:</span>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">{state.reason}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Status */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <BellIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">แจ้งเตือนผู้ปกครองแล้ว</h3>
              <p className="text-sm text-blue-700 mb-2">
                ส่งข้อความแจ้งเหตุผลการไม่อนุมัติผ่าน LINE ไปยังคุณ{state.slip.parentName} แล้ว
              </p>
              <p className="text-xs text-blue-600">
                💡 ผู้ปกครองจะได้รับคำแนะนำในการแก้ไขและส่งสลิปใหม่
              </p>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <h3 className="font-medium text-yellow-900 mb-3">📋 ขั้นตอนต่อไป</h3>
          <div className="space-y-2 text-sm text-yellow-800">
            <div className="flex items-start">
              <span className="w-6 h-6 bg-yellow-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">1</span>
              <span>ผู้ปกครองได้รับการแจ้งเตือนพร้อมเหตุผล</span>
            </div>
            <div className="flex items-start">
              <span className="w-6 h-6 bg-yellow-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">2</span>
              <span>ผู้ปกครองแก้ไขปัญหาตามที่แจ้ง</span>
            </div>
            <div className="flex items-start">
              <span className="w-6 h-6 bg-yellow-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">3</span>
              <span>ส่งสลิปการโอนใหม่เข้าระบบ</span>
            </div>
            <div className="flex items-start">
              <span className="w-6 h-6 bg-yellow-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">4</span>
              <span>รอการตรวจสอบครั้งใหม่</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/admin/payments')}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
            กลับไปรายการตรวจสอบ
          </button>
          
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon className="h-5 w-5 mr-2 rotate-180" />
            กลับไปหน้าหลัก
          </button>
        </div>

        {/* Contact Support */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-2">
            หากผู้ปกครองมีข้อสงสัยเพิ่มเติม
          </p>
          <p className="text-sm font-medium text-gray-700">
            📞 สามารถติดต่อ: 081-XXX-XXXX
          </p>
          <p className="text-xs text-gray-500 mt-1">
            เวลาทำการ: จันทร์-เสาร์ 08:00-17:00 น.
          </p>
        </div>
      </div>
    </div>
  )
}